import {
  IconGripVertical,
  IconPlus,
  IconRocket,
  IconTrash,
} from "@tabler/icons-react";
import { DraggableStepItem } from "../AgentWizard";
import DynamicFlowStepComponent from "./dynamicFlowStep";
import { Accordion, Badge, Button, Col, Row, Form } from "react-bootstrap";
import { memo, useCallback, useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import Swal from "sweetalert2";

const DraggableFlowItem = ({ flow, index, moveFlow, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "FLOW_ITEM",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "FLOW_ITEM",
    hover: (item) => {
      if (item.index !== index) {
        moveFlow(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
      }}
    >
      {children}
    </div>
  );
};

const SpecialFlowComponent = memo(
  ({
    flow,
    index,
    moveFlow,
    removeSpecialFlow,
    register,
    watch,
    setValue,
    handleAIFill,
    ImageSelect,
    NEXT_STEP_OPIONS,
    getFlowStepsForMode,
  }) => {
    const flowName =
      watch(`specialFlows.${index}.name`) || `Fluxo Especial ${index + 1}`;

    // ✅ CORREÇÃO: Obter valor inicial diretamente do formulário
    const getCurrentSpecialFlowSteps = () => {
      const currentSteps = watch(`specialFlows.${index}.steps`);
      if (currentSteps && currentSteps.length > 0) {
        return currentSteps;
      }
      return [
        {
          id: "special_step_" + Date.now(),
          key: "greeting",
          label: "Saudação",
          icon: "👋",
          description: "Como o agente vai cumprimentar cada pessoa que chegar",
          order: 0,
        },
      ];
    };

    const [specialFlowSteps, setSpecialFlowSteps] = useState(
      getCurrentSpecialFlowSteps
    );

    const updateSpecialFormSteps = useCallback(
      (newSteps) => {
        // ❌ COMENTAR ESTA VERIFICAÇÃO - não é mais necessária sem preview
        /*
        if (
          JSON.stringify(newSteps) !==
          JSON.stringify(watch(`specialFlows.${index}.steps`))
        ) {
          setValue(`specialFlows.${index}.steps`, newSteps, {
            shouldValidate: false,
          });
        }
        */

        // ✅ VERSÃO SIMPLIFICADA
        setValue(`specialFlows.${index}.steps`, newSteps, {
          shouldValidate: false,
        });
      },
      [setValue, index] // ✅ REMOVER watch das dependências
    );

    useEffect(() => {
      updateSpecialFormSteps(specialFlowSteps);
    }, [specialFlowSteps, updateSpecialFormSteps]);

    const addNewSpecialStep = () => {
      const newStep = {
        id: "special_step_" + Date.now(),
        key: `step_${specialFlowSteps.length}`,
        label: `Etapa ${specialFlowSteps.length + 1}`,
        icon: "📝",
        description: "Nova etapa do atendimento especial",
        order: specialFlowSteps.length,
      };

      setSpecialFlowSteps([...specialFlowSteps, newStep]);
    };

    const removeSpecialStep = (stepId) => {
      if (specialFlowSteps.length <= 1) {
        Swal.fire({
          icon: "warning",
          title: "Não é possível remover",
          text: "Deve haver pelo menos uma etapa no fluxo.",
        });
        return;
      }

      const newSteps = specialFlowSteps.filter((step) => step.id !== stepId);
      const reorderedSteps = newSteps.map((step, stepIndex) => ({
        ...step,
        order: stepIndex,
      }));
      setSpecialFlowSteps(reorderedSteps);
    };

    const moveSpecialFlowStep = useCallback(
      (dragIndex, hoverIndex) => {
        const newSteps = [...specialFlowSteps];
        const draggedStep = newSteps[dragIndex];
        newSteps.splice(dragIndex, 1);
        newSteps.splice(hoverIndex, 0, draggedStep);

        const reorderedSteps = newSteps.map((step, stepIndex) => ({
          ...step,
          order: stepIndex,
        }));

        setSpecialFlowSteps(reorderedSteps);
      },
      [specialFlowSteps]
    );

    const handleRemove = useCallback(
      (e) => {
        e.stopPropagation();
        removeSpecialFlow(index);
      },
      [removeSpecialFlow, index]
    );

    return (
      <DraggableFlowItem flow={flow} index={index} moveFlow={moveFlow}>
        <Accordion className="mb-3">
          <Accordion.Item eventKey={`special-flow-${index}`}>
            <Accordion.Header>
              <div className="d-flex align-items-center justify-content-between w-100 me-3">
                <div className="d-flex align-items-center">
                  <IconGripVertical className="me-2 text-muted" size={16} />
                  <IconRocket className="me-2 text-warning" size={16} />
                  <strong>{flowName}</strong>
                  <Badge bg="warning" className="ms-2">
                    Especial
                  </Badge>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemove}
                >
                  <IconTrash size={14} />
                </Button>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Nome do Fluxo</Form.Label>
                    <Form.Control
                      {...register(`specialFlows.${index}.name`)}
                      placeholder="Ex: Fluxo Tráfego Pago"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Palavras-chave (separadas por vírgula)
                    </Form.Label>
                    <Form.Control
                      {...register(`specialFlows.${index}.triggers`)}
                      placeholder="quero mais informações, pode me dar mais informações"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Etapas do Fluxo Especial</h6>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addNewSpecialStep}
                >
                  <IconPlus className="me-1" size={16} />
                  Nova Etapa
                </Button>
              </div>

              {specialFlowSteps.map((step, stepIndex) => (
                <DraggableStepItem
                  key={step.id}
                  step={step}
                  index={stepIndex}
                  moveStep={moveSpecialFlowStep}
                >
                  <DynamicFlowStepComponent
                    step={step}
                    index={stepIndex}
                    flowType="special"
                    flowIndex={index}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    handleAIFill={handleAIFill}
                    ImageSelect={ImageSelect}
                    NEXT_STEP_OPIONS={NEXT_STEP_OPIONS}
                    flowMode="advanced"
                    onRemove={() => removeSpecialStep(step.id)}
                    canRemove={specialFlowSteps.length > 1}
                  />
                </DraggableStepItem>
              ))}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </DraggableFlowItem>
    );
  }
);

SpecialFlowComponent.displayName = "SpecialFlowComponent";
export default SpecialFlowComponent;
